import type { TonoDeEtiqueta } from '@/components/ui/etiqueta';
import { formatoNumero, formatoPorcentaje } from '@/lib/utils/formato-precio';
import { explicacionDeSalida, tituloDeSalida } from './salidas-sin-precio';
import { formatoPrecio, pisoEnTexto, techoEnTexto } from '../components/barra-de-rangos';
import {
  cualesCumplir,
  esDeTramo,
  terminacion,
  VERBOS,
  type PedidoDelRecorrido,
  type RecorridoDelPrecio,
  type ReglaDelRecorrido,
  type Tiempo,
} from './recorrido-del-precio';
import type { ClaveDeSalida } from '../types/pricing-rules.types';
import { LA_DE } from './jerarquia';

/**
 * La explicación escrita para quien decide (spec 227): el título, el resultado,
 * el porqué en una frase, los avisos y, sin precio posible, las opciones. Sale
 * de un `RecorridoDelPrecio` en funciones puras; `PorQueEstePrecio` sólo lo
 * dibuja. La cuenta paso a paso sigue en `cuentaDelPrecio`, y el dibujo en
 * `dibujoDelPrecio`. Nada se recalcula: el precio lo decidió el backend.
 *
 * El nombre de la tienda más barata («Dismac») llega con el plan 2 (el
 * mercado): hasta entonces las frases dicen «el más barato (2.549,00)».
 */

const CASI = 0.005;
/** Desde cuánto un cambio de precio es «de golpe» (spec 227 §4). */
export const UMBRAL_DE_SALTO = 30;

const fmt = (n: number) => formatoPrecio(n);
const num = (n: number) => formatoNumero(n);
/** «15 %», «12,5 %»: el porcentaje de una regla, sin ceros de más. */
const pct = (n: number) => `${num(Math.round(n * 10) / 10)} %`;
const redondo = (n: number) => Math.round(n * 100) / 100;
/** «−23,5 %», «+59,6 %»: el cambio, con una decimal (el formato único del pulido). */
const pctConSigno = (n: number) => formatoPorcentaje(n, { signo: true });

/** El margen sobre el precio, sin redondear: (precio − costo) ÷ precio × 100. */
function margenCrudo(precio: number, costo: number | null): number | null {
  if (costo == null || !(precio > 0)) return null;
  return ((precio - costo) / precio) * 100;
}

// ── Las piezas de texto ────────────────────────────────────────────────────

/** Un pedazo de frase; los números van en negrita. */
export interface Trozo {
  texto: string;
  negrita?: boolean;
}
const t = (texto: string): Trozo => ({ texto });
const b = (texto: string): Trozo => ({ texto, negrita: true });
/** La frase entera, sin marcas: para los tests y el lector de pantalla. */
export const textoDe = (trozos: Trozo[]) => trozos.map((x) => x.texto).join('');

// ── El título ──────────────────────────────────────────────────────────────

/** El título de la tarjeta; el tiempo va aparte, en una etiqueta (spec 227 §1). */
export const TITULOS: Record<Tiempo, string> = {
  HOY: 'Por qué este precio',
  SI_CORRIERA: 'Por qué este precio',
  PASO: 'Por qué quedó en este precio',
};

export const ETIQUETA_DE_TIEMPO: Record<Tiempo, string | null> = {
  HOY: 'hoy',
  SI_CORRIERA: 'si corriera hoy',
  PASO: null,
};

/**
 * Lo que «¿Y si…?» ya contó sobre el catálogo: cuántos cambiarían y cuántos más
 * quedarían sin precio posible. null = la pantalla no lo trae.
 */
export interface ConteosDeLaPrueba {
  cambian: number | null;
  masSinPrecio: number | null;
  /**
   * Sobre qué se contó: «de la categoría Audio», «de la marca Sony»
   * (`alcanceDelFiltro`). null o ausente = todo el catálogo.
   */
  alcance?: string | null;
}

/**
 * «¿Y si…?» cuenta sobre el mismo filtro de categoría o marca con que se prueba
 * la regla (`y-si.tsx`): el título lo dice, para no leerse como el catálogo entero.
 */
export function alcanceDelFiltro(
  f: { category?: string; brand?: string } | null | undefined
): string | null {
  if (f?.category) return `de la categoría ${f.category}`;
  if (f?.brand) return `de la marca ${f.brand}`;
  return null;
}

export interface TituloDelPrecio {
  texto: string;
  etiquetas: Array<{ texto: string; tono: TonoDeEtiqueta }>;
  /** El nombre de la sección para el lector de pantalla: «Por qué este precio, hoy». */
  nombre: string;
}

/** «1 producto», «7 productos de la categoría Audio». */
const productos = (n: number, alcance: string | null) =>
  `${num(n)} ${n === 1 ? 'producto' : 'productos'}${alcance ? ` ${alcance}` : ''}`;

/**
 * «Por qué este precio» + «hoy»; en «¿Y si…?» y Nueva regla, un solo título:
 * «Si encendés «‹regla›»», con «simulación: nada cambió todavía» y, si la
 * pantalla los trae, cuántos productos afecta.
 */
export function tituloDelPrecio(
  r: RecorridoDelPrecio,
  tiempo: Tiempo,
  conteos: ConteosDeLaPrueba | null = null
): TituloDelPrecio {
  if (r.comparacion) {
    const texto = `Si encendés «${r.comparacion.regla}»`;
    const etiquetas: TituloDelPrecio['etiquetas'] = [
      { texto: 'simulación: nada cambió todavía', tono: 'info' },
    ];
    const mas = conteos?.masSinPrecio ?? null;
    const cambian = conteos?.cambian ?? null;
    const alcance = conteos?.alcance ?? null;
    if (r.estado === 'SIN_PRECIO_POSIBLE' && mas != null && mas > 0)
      etiquetas.push({
        texto: `${productos(mas, alcance)} ${mas === 1 ? 'quedaría' : 'quedarían'} con un conflicto entre reglas`,
        tono: 'neutro',
      });
    else if (cambian != null)
      etiquetas.push({ texto: `afectaría a ${productos(cambian, alcance)}`, tono: 'neutro' });
    return { texto, etiquetas, nombre: texto };
  }
  const etiqueta = ETIQUETA_DE_TIEMPO[tiempo];
  return {
    texto: TITULOS[tiempo],
    etiquetas: etiqueta ? [{ texto: etiqueta, tono: 'neutro' }] : [],
    nombre: etiqueta ? `${TITULOS[tiempo]}, ${etiqueta}` : TITULOS[tiempo],
  };
}

// ── El resultado ───────────────────────────────────────────────────────────

export interface DatoDelResultado {
  clave: 'CAMBIO' | 'CONTRA_TUS_REGLAS' | 'MARGEN' | 'MAS_BARATO' | 'BLOQUEO';
  texto: string;
  tono: TonoDeEtiqueta;
}

export interface ResultadoDelPrecio {
  /** «Queda en», «Quedaría en», «Quedó en», «Sigue en»; null sin precio posible. */
  prefijo: string | null;
  /** «3.242,99», o «Sin precio posible». */
  precio: string;
  sinPrecio: boolean;
  datos: DatoDelResultado[];
}

/** «27 % arriba del más barato», «0,99 arriba del más barato», «igual al más barato». */
export function frenteAlMasBarato(precio: number, masBarato: number): string {
  const d = precio - masBarato;
  if (Math.abs(d) < CASI) return 'igual al más barato';
  const lado = d > 0 ? 'arriba' : 'debajo';
  const p = Math.round((Math.abs(d) / masBarato) * 100);
  return p >= 1
    ? `${num(p)} % ${lado} del más barato`
    : `${fmt(redondo(Math.abs(d)))} ${lado} del más barato`;
}

function datoDeMargen(precio: number, costo: number | null): DatoDelResultado | null {
  const m = margenCrudo(precio, costo);
  if (m == null) return null;
  return m >= 0
    ? { clave: 'MARGEN', texto: `ganás ${formatoPorcentaje(m)}`, tono: 'ok' }
    : { clave: 'MARGEN', texto: `perdés ${formatoPorcentaje(-m)}`, tono: 'bloqueo' };
}

/** «↓ baja 997,01 desde 4.240,00 (−23,5 %)». */
function datoDeCambio(queda: number, hoy: number): DatoDelResultado {
  const d = redondo(queda - hoy);
  const porcentaje = hoy > 0 ? ` (${pctConSigno((d / hoy) * 100)})` : '';
  return {
    clave: 'CAMBIO',
    texto: `${d > 0 ? '↑ sube' : '↓ baja'} ${fmt(Math.abs(d))} desde ${fmt(hoy)}${porcentaje}`,
    tono: d > 0 ? 'ok' : 'baja',
  };
}

/** Donde queda: el precio que calculó, o la salida que se eligió. */
const quedaDe = (r: RecorridoDelPrecio) => r.queda ?? r.elegida?.precio ?? null;

function resultadoDe(r: RecorridoDelPrecio, tiempo: Tiempo): ResultadoDelPrecio | null {
  if (r.estado === 'SIN_CALCULO') return null;
  const queda = quedaDe(r);
  if (queda == null)
    return {
      prefijo: null,
      precio: 'Conflicto entre reglas',
      sinPrecio: true,
      datos: [
        {
          clave: 'BLOQUEO',
          texto: 'tus reglas piden cosas que no se pueden cumplir juntas',
          tono: 'bloqueo',
        },
      ],
    };

  const c = r.comparacion;
  if (c && c.con != null) {
    if (c.sin != null && Math.abs(c.con - c.sin) < CASI)
      return {
        prefijo: 'Sigue en',
        precio: fmt(queda),
        sinPrecio: false,
        datos: [{ clave: 'CONTRA_TUS_REGLAS', texto: '= esta regla no lo cambia', tono: 'neutro' }],
      };
    const datos: DatoDelResultado[] = [];
    if (c.sin != null) {
      const d = redondo(c.con - c.sin);
      datos.push({
        clave: 'CONTRA_TUS_REGLAS',
        texto:
          d > 0
            ? `↑ ${fmt(d)} más que con tus reglas de hoy (${fmt(c.sin)})`
            : `↓ ${fmt(-d)} menos que con tus reglas de hoy (${fmt(c.sin)})`,
        tono: d > 0 ? 'ok' : 'baja',
      });
    }
    const margen = datoDeMargen(queda, r.costo);
    if (margen) datos.push(margen);
    if (r.hoy != null && Math.abs(queda - r.hoy) >= CASI) {
      const d = redondo(queda - r.hoy);
      // «igual baja»: la regla nueva lo sube, pero aun así queda debajo de hoy.
      const aunAsi = c.sin != null && Math.sign(c.con - c.sin) !== Math.sign(d);
      datos.push({
        clave: 'CAMBIO',
        texto: `${aunAsi ? 'igual ' : ''}${d > 0 ? 'sube' : 'baja'} ${fmt(Math.abs(d))} desde ${fmt(r.hoy)}`,
        tono: 'neutro',
      });
    }
    return { prefijo: 'Quedaría en', precio: fmt(queda), sinPrecio: false, datos };
  }

  const sigue = tiempo !== 'PASO' && r.hoy != null && Math.abs(queda - r.hoy) < CASI;
  const datos: DatoDelResultado[] = [];
  if (r.hoy != null && !sigue && Math.abs(queda - r.hoy) >= CASI)
    datos.push(datoDeCambio(queda, r.hoy));
  const margen = datoDeMargen(queda, r.costo);
  if (margen) datos.push(margen);
  if (r.masBarato != null)
    datos.push({
      clave: 'MAS_BARATO',
      texto: frenteAlMasBarato(queda, r.masBarato),
      tono: 'neutro',
    });
  return {
    prefijo: sigue ? 'Sigue en' : `${VERBOS[tiempo]} en`,
    precio: fmt(queda),
    sinPrecio: false,
    datos,
  };
}

// ── El porqué ──────────────────────────────────────────────────────────────

/** Lo que pidió la estrategia, en una frase sin punto final. */
function pedidoEnTrozos(p: PedidoDelRecorrido | null): Trozo[] {
  if (!p || p.precio == null) return [t('No se guardó cuánto pedía la estrategia')];
  if (p.origen === 'HOY')
    return [t('Sin estrategia, se parte del precio de hoy ('), b(fmt(p.precio)), t(')')];
  if (p.origen === 'VUELTA') return [t('Volvía a su precio de antes ('), b(fmt(p.precio)), t(')')];
  const c = p.como;
  switch (c?.metodo) {
    case 'MATCH_LOWEST':
      return [t('Tu estrategia quería igualar al más barato ('), b(fmt(c.masBarato)), t(')')];
    case 'UNDERCUT_PERCENT':
      return [
        t(`Tu estrategia quería quedar ${pct(c.porcentaje)} debajo del más barato (`),
        b(fmt(c.masBarato)),
        t('), o sea '),
        b(fmt(p.precio)),
      ];
    case 'MARGIN_TARGET':
      return [
        t(
          `Tu estrategia quería ganar ${pct(c.margen)}${p.usaMargenPropio ? ' (el margen propio del producto)' : ''} con tu costo (`
        ),
        b(fmt(c.costo)),
        t('), o sea '),
        b(fmt(p.precio)),
      ];
    case 'DISCOUNT_PERCENT':
      return [
        t(`Tu estrategia quería bajar ${pct(c.porcentaje)} del precio de lista (`),
        b(fmt(c.lista)),
        t('), o sea '),
        b(fmt(p.precio)),
      ];
    default:
      return [
        t(`Tu estrategia${p.estrategia ? ` «${p.estrategia}»` : ''} pedía `),
        b(fmt(p.precio)),
      ];
  }
}

/** El borde de la regla que toca el precio que dejan las reglas: su piso o su techo. */
function limiteQueToca(
  regla: ReglaDelRecorrido,
  precio: number | null
): { lado: 'PISO' | 'TECHO'; valor: number } | null {
  const { min, max } = regla;
  if (min != null && max != null)
    return precio != null && Math.abs(max - precio) < Math.abs(min - precio)
      ? { lado: 'TECHO', valor: max }
      : { lado: 'PISO', valor: min };
  if (min != null) return { lado: 'PISO', valor: min };
  if (max != null) return { lado: 'TECHO', valor: max };
  return null;
}

const enTexto = (l: { lado: 'PISO' | 'TECHO'; valor: number }) =>
  l.lado === 'PISO' ? pisoEnTexto(l.valor) : techoEnTexto(l.valor);

/** El piso más alto de un margen mínimo (una regla dura), si hay. */
function pisoDelMargen(r: RecorridoDelPrecio): number | null {
  const pisos = (r.reglas ?? [])
    .filter((x) => esDeTramo(x) && x.duro && x.tipo === 'MARGEN_MINIMO' && x.min != null)
    .map((x) => x.min!);
  return pisos.length ? Math.max(...pisos) : null;
}

/** El margen que pide un piso: «15» (para «ganás 15 %»). null sin costo. */
const margenDelPiso = (piso: number, costo: number | null) => {
  const m = margenCrudo(piso, costo);
  return m == null ? null : pct(m);
};

/**
 * Lote B2: la regla que decide pisa a otras del mismo tipo (es más específica,
 * aunque pida menos). « Decide ésta y no «Margen Minimo Global»: es la de este
 * producto, más específica.» Vacío si no pisa a ninguna.
 */
function porLaJerarquia(decide: ReglaDelRecorrido, r: RecorridoDelPrecio, sujeto: string): Trozo[] {
  const pisadas = (r.pisadas ?? []).filter((p) => p.pisadoPor.ruleId === decide.ruleId);
  if (pisadas.length === 0) return [];
  const nombres = [...new Set(pisadas.map((p) => `«${p.ruleName}»`))].join(' ni ');
  return [
    t(` ${sujeto} y no ${nombres}: es ${LA_DE[pisadas[0].pisadoPor.alcance]}, `),
    b('más específica'),
    t('.'),
  ];
}

/** «Estabas por debajo…», «Tu estrategia quería…», y qué hizo cada regla. */
function porqueDeHoy(r: RecorridoDelPrecio): Trozo[] {
  const out: Trozo[] = [];
  const pide = r.pedido?.origen === 'ESTRATEGIA' ? r.pedido.precio : null;
  const queda = quedaDe(r);
  const despues = r.redondeo ? r.redondeo.desde : queda;
  const decide = r.reglas?.find((x) => x.manda) ?? null;
  const perdia = pide != null && r.costo != null && pide < r.costo - CASI;
  const mb = r.masBarato;
  const perdiaEnTrozos = () => [
    t('Pero eso es menos de lo que te cuesta ('),
    b(fmt(r.costo!)),
    t(')'),
  ];

  if (decide || r.reglaQueLimito) {
    out.push(...pedidoEnTrozos(r.pedido), t('. '));
    if (perdia) out.push(...perdiaEnTrozos(), t('. '));
    const lim = decide ? limiteQueToca(decide, despues) : null;
    if (decide && lim) {
      out.push(
        t('Tu regla '),
        b(`«${decide.ruleName}»`),
        t(` no lo deja ${lim.lado === 'PISO' ? 'bajar de' : 'pasar de'} `),
        b(enTexto(lim))
      );
      const m = decide.tipo === 'MARGEN_MINIMO' ? margenDelPiso(lim.valor, r.costo) : null;
      if (lim.lado === 'PISO' && m) out.push(t(`, el precio más bajo donde ganás ${m}`));
      out.push(t('.'), ...porLaJerarquia(decide, r, 'Decide ésta'));
    } else
      out.push(t('Tu regla '), b(`«${decide?.ruleName ?? r.reglaQueLimito}»`), t(' lo limitó.'));
  } else {
    // La estrategia decide sola.
    const como = r.pedido?.como;
    const abajoDelMercado =
      pide != null &&
      mb != null &&
      r.hoy != null &&
      r.hoy < mb - CASI &&
      (como?.metodo === 'MATCH_LOWEST' || como?.metodo === 'UNDERCUT_PERCENT');
    if (abajoDelMercado) {
      const salto =
        queda != null && r.hoy! > 0 && ((queda - r.hoy!) / r.hoy!) * 100 >= UMBRAL_DE_SALTO;
      out.push(
        t(`Estabas ${salto ? 'muy ' : ''}por debajo del mercado: el más barato lo vende a `),
        b(fmt(mb)),
        t(' y vos a '),
        b(fmt(r.hoy!)),
        t('. ')
      );
      if (como?.metodo === 'MATCH_LOWEST') out.push(t('Tu estrategia lo iguala'));
      else
        out.push(
          t(`Tu estrategia lo deja ${pct(como!.porcentaje)} debajo (`),
          b(fmt(pide)),
          t(')')
        );
    } else out.push(...pedidoEnTrozos(r.pedido));
    if (perdia) out.push(t('. '), ...perdiaEnTrozos());
    out.push(t(', y '), b('ninguna regla lo limita'));
    const piso = pisoDelMargen(r);
    const m = piso != null ? margenDelPiso(piso, r.costo) : null;
    if (piso != null && m && queda != null && queda > piso + CASI)
      out.push(t(`: tu margen mínimo (${m}) pedía apenas ${pisoEnTexto(piso)}`));
    out.push(t('.'));
  }

  if (r.redondeo) {
    out.push(t(` El redondeo ${terminacion(r.redondeo.hasta)}`));
    // Sin regla que decida, el redondeo puede pasarlo del más barato: se dice.
    if (!decide && mb != null && r.redondeo.desde <= mb + CASI && r.redondeo.hasta > mb + CASI)
      out.push(t(', así que queda '), b(frenteAlMasBarato(r.redondeo.hasta, mb)));
    out.push(t('.'));
  }
  return out;
}

/** Quién pone un borde: «Tu margen», «La regla nueva», «Tu regla «X»». */
function quienPone(regla: ReglaDelRecorrido | undefined, mayuscula: boolean): string {
  const texto = !regla
    ? 'una regla'
    : regla.probando
      ? 'la regla nueva'
      : regla.duro && regla.tipo === 'MARGEN_MINIMO'
        ? 'tu margen'
        : `tu regla «${regla.ruleName}»`;
  return mayuscula ? texto.charAt(0).toUpperCase() + texto.slice(1) : texto;
}

/** De dónde sale el borde: «(ganar 15 % con tu costo de 2.756,00)». */
function detalleDelBorde(
  regla: ReglaDelRecorrido | undefined,
  lado: 'PISO' | 'TECHO',
  valor: number,
  r: RecorridoDelPrecio
): string {
  if (!regla) return '';
  if (regla.tipo === 'MARGEN_MINIMO' && lado === 'PISO' && r.costo != null) {
    const m = margenDelPiso(valor, r.costo);
    return m ? ` (ganar ${m} con tu costo de ${fmt(r.costo)})` : '';
  }
  if (regla.tipo === 'SOBRE_EL_MAS_BARATO' && lado === 'TECHO' && r.masBarato != null)
    return ` (${pct((valor / r.masBarato - 1) * 100)} arriba del más barato, ${fmt(r.masBarato)})`;
  // Lote B2 (D7): el espejo, un piso contra el más barato.
  if (regla.tipo === 'BAJO_EL_MAS_BARATO' && lado === 'PISO' && r.masBarato != null)
    return ` (${pct((1 - valor / r.masBarato) * 100)} debajo del más barato, ${fmt(r.masBarato)})`;
  if (regla.tipo === 'MOVIMIENTO' && r.hoy != null && r.hoy > 0)
    return ` (mover hasta ${pct((Math.abs(valor - r.hoy) / r.hoy) * 100)} desde hoy, ${fmt(r.hoy)})`;
  return '';
}

/**
 * «No se movería solo: iría a Por decidir para que elijas una de estas:». Con
 * `sinOpciones` (la ventana «Decidir», que dibuja su propia lista debajo) no
 * promete «estas»: «Elegí abajo qué hacer.» (auditoría M1).
 */
function porDecidir(r: RecorridoDelPrecio, tiempo: Tiempo, sinOpciones = false): Trozo[] {
  const con = (r.salidas?.length ?? 0) > 0;
  // «El Colchón no se movería solo»: con el nombre si la fuente lo trae (Análisis no).
  const quien = (verbo: string) =>
    r.producto ? `${r.producto} ${verbo}` : verbo.charAt(0).toUpperCase() + verbo.slice(1);
  if (tiempo === 'PASO')
    return r.elegida
      ? [t(`${quien('no se movió solo')}: en `), b('Por decidir'), t(' se eligió una de estas:')]
      : [
          t(`${quien('no se movió solo')}: esperó en `),
          b('Por decidir'),
          t(con ? ' con estas opciones:' : '.'),
        ];
  if (r.comparacion || tiempo === 'SI_CORRIERA')
    return [
      t(`${quien('no se movería solo')}: iría a `),
      b('Por decidir'),
      t(con ? (sinOpciones ? '. Elegí abajo qué hacer.' : ' para que elijas una de estas:') : '.'),
    ];
  return [
    t(`${quien('no se mueve solo')}: te espera en `),
    b('Por decidir'),
    t(con ? (sinOpciones ? '. Elegí abajo qué hacer.' : ' para que elijas una de estas:') : '.'),
  ];
}

function porqueSinPrecio(r: RecorridoDelPrecio, tiempo: Tiempo, sinOpciones = false): Trozo[] {
  const min = r.cruce?.min ?? null;
  const max = r.cruce?.max ?? null;
  if (min == null || max == null)
    return [
      t('Tus reglas no se pueden cumplir todas a la vez. '),
      ...porDecidir(r, tiempo, sinOpciones),
    ];
  const tramos = (r.reglas ?? []).filter(esDeTramo);
  const piso = tramos.find((x) => x.min != null && Math.abs(x.min - min) < CASI);
  const techo = tramos.find((x) => x.max != null && Math.abs(x.max - max) < CASI);
  return [
    t(`${quienPone(piso, true)} pide `),
    b(`al menos ${pisoEnTexto(min)}`),
    t(`${detalleDelBorde(piso, 'PISO', min, r)}. ${quienPone(techo, true)} pide `),
    b(`como mucho ${techoEnTexto(max)}`),
    t(`${detalleDelBorde(techo, 'TECHO', max, r)}. El mínimo queda por encima del máximo: `),
    b(`ningún precio cumple ${cualesCumplir(r.reglas)}`),
    t('. '),
    ...porDecidir(r, tiempo, sinOpciones),
  ];
}

/** Lo que pide la regla que se prueba: «Ganar al menos 20 % con tu costo (2.756,00) pide 3.445,00». */
function pideLaReglaNueva(
  regla: ReglaDelRecorrido,
  lim: { lado: 'PISO' | 'TECHO'; valor: number },
  r: RecorridoDelPrecio
): Trozo[] {
  if (regla.tipo === 'MARGEN_MINIMO' && lim.lado === 'PISO' && r.costo != null) {
    const m = margenDelPiso(lim.valor, r.costo);
    if (m)
      return [
        t(`Ganar al menos ${m} con tu costo (`),
        b(fmt(r.costo)),
        t(') pide '),
        b(enTexto(lim)),
      ];
  }
  if (regla.tipo === 'SOBRE_EL_MAS_BARATO' && lim.lado === 'TECHO' && r.masBarato != null)
    return [
      t(`No quedar más de ${pct((lim.valor / r.masBarato - 1) * 100)} arriba del más barato (`),
      b(fmt(r.masBarato)),
      t(') pide como mucho '),
      b(enTexto(lim)),
    ];
  return [
    t(`La regla nueva no lo deja ${lim.lado === 'PISO' ? 'bajar de' : 'pasar de'} `),
    b(enTexto(lim)),
  ];
}

/** Lo que deja la regla nueva: «deja mover el precio hasta 40 %… de 2.544,00 a 5.936,00». */
function dejaLaReglaNueva(nueva: ReglaDelRecorrido[], r: RecorridoDelPrecio): Trozo[] {
  const mins = nueva.filter((x) => x.min != null).map((x) => x.min!);
  const maxs = nueva.filter((x) => x.max != null).map((x) => x.max!);
  const min = mins.length ? Math.max(...mins) : null;
  const max = maxs.length ? Math.min(...maxs) : null;
  const tramo =
    min != null && max != null
      ? `de ${pisoEnTexto(min)} a ${techoEnTexto(max)}`
      : min != null
        ? `desde ${pisoEnTexto(min)}`
        : `hasta ${techoEnTexto(max!)}`;
  const tipo = nueva[0].tipo;
  const hoy = r.hoy;
  if (tipo === 'MOVIMIENTO' && hoy != null && hoy > 0) {
    const arriba = max != null ? pct(((max - hoy) / hoy) * 100) : null;
    const abajo = min != null ? pct(((hoy - min) / hoy) * 100) : null;
    const como =
      arriba && abajo
        ? arriba === abajo
          ? `hasta ${arriba} para arriba o para abajo`
          : `hasta ${arriba} para arriba y ${abajo} para abajo`
        : arriba
          ? `hasta ${arriba} para arriba`
          : `hasta ${abajo} para abajo`;
    return [t(`La regla nueva deja mover el precio ${como} desde hoy (${fmt(hoy)}): `), b(tramo)];
  }
  if (tipo === 'MARGEN_MINIMO' && min != null) {
    const m = margenDelPiso(min, r.costo);
    if (m) return [t(`La regla nueva pide ganar al menos ${m}: `), b(tramo)];
  }
  if (tipo === 'SOBRE_EL_MAS_BARATO' && max != null && r.masBarato != null)
    return [
      t(
        `La regla nueva deja quedar hasta ${pct((max / r.masBarato - 1) * 100)} arriba del más barato (${fmt(r.masBarato)}): `
      ),
      b(tramo),
    ];
  return [t('La regla nueva deja '), b(tramo)];
}

/** «¿Y si…?» y Nueva regla, con precio: qué cambia la regla que se prueba. */
function porqueDeLaPrueba(r: RecorridoDelPrecio): Trozo[] {
  const c = r.comparacion!;
  const con = c.con!;
  const nueva = (r.reglas ?? []).filter((x) => x.probando && esDeTramo(x));
  const redondeo = r.redondeo ? [t(` El redondeo ${terminacion(r.redondeo.hasta)}.`)] : [];

  if (c.sin != null && Math.abs(con - c.sin) < CASI) {
    if (nueva.length === 0)
      return [t('La regla nueva no le pone un límite a este producto, así que no lo toca.')];
    return [
      ...dejaLaReglaNueva(nueva, r),
      t('. El precio que ya dejan tus reglas ('),
      b(fmt(con)),
      t(') está adentro, así que no lo toca.'),
    ];
  }

  const decide = nueva.find((x) => x.manda);
  const despues = r.redondeo ? r.redondeo.desde : con;
  const lim = decide ? limiteQueToca(decide, despues) : null;
  if (decide && lim) {
    const out = [...pideLaReglaNueva(decide, lim, r), t('. ')];
    const otras = (r.reglas ?? []).filter(
      (x) => !x.probando && esDeTramo(x) && (lim.lado === 'PISO' ? x.min != null : x.max != null)
    );
    const otra =
      otras.length === 0
        ? null
        : otras.reduce((a, x) =>
            lim.lado === 'PISO' ? (x.min! > a.min! ? x : a) : x.max! < a.max! ? x : a
          );
    if (otra) {
      const valor = lim.lado === 'PISO' ? otra.min! : otra.max!;
      const m =
        otra.tipo === 'MARGEN_MINIMO' && lim.lado === 'PISO' ? margenDelPiso(valor, r.costo) : null;
      out.push(
        t(
          `Eso es más ${lim.lado === 'PISO' ? 'alto que el piso' : 'bajo que el techo'} de hoy (${m ? `${m}, ` : `«${otra.ruleName}», `}`
        ),
        b(lim.lado === 'PISO' ? pisoEnTexto(valor) : techoEnTexto(valor)),
        t(
          `). Entre las reglas que rigen y piden un ${lim.lado === 'PISO' ? 'mínimo' : 'máximo'}, `
        ),
        b('decide la más exigente'),
        t('.')
      );
    } else if (c.sin != null)
      out.push(
        t(
          `Eso es más ${lim.lado === 'PISO' ? 'alto' : 'bajo'} que lo que dejan tus reglas de hoy (`
        ),
        b(fmt(c.sin)),
        t(').')
      );
    out.push(...porLaJerarquia(decide, r, 'Para este producto rige la nueva'));
    return [...out, ...redondeo];
  }
  if (r.redondeo?.probando)
    return [t(`La regla nueva redondea: ${terminacion(r.redondeo.hasta)}.`)];
  return c.sin != null
    ? [t('Con la regla nueva pasa de '), b(fmt(c.sin)), t(' a '), b(fmt(con)), t('.')]
    : [t('Con la regla nueva quedaría en '), b(fmt(con)), t('.')];
}

function porqueDe(r: RecorridoDelPrecio, tiempo: Tiempo, sinOpciones: boolean): Trozo[] {
  if (r.estado === 'SIN_CALCULO') return [];
  if (r.estado === 'SIN_PRECIO_POSIBLE') return porqueSinPrecio(r, tiempo, sinOpciones);
  if (r.comparacion && r.comparacion.con != null) return porqueDeLaPrueba(r);
  return porqueDeHoy(r);
}

// ── Los avisos ─────────────────────────────────────────────────────────────

export interface AvisoDelPrecio {
  clave: 'PERDIDA' | 'DEBAJO_DEL_COSTO' | 'SALTO' | 'NADA_QUE_DECIDIR';
  tono: 'alerta' | 'bloqueo' | 'ok';
  titulo: string;
  texto: string;
}

function avisosDe(r: RecorridoDelPrecio): AvisoDelPrecio[] {
  if (r.estado === 'SIN_CALCULO') return [];
  const out: AvisoDelPrecio[] = [];
  const queda = quedaDe(r);
  const costo = r.costo;
  const c = r.comparacion;

  // La pérdida se dice en la explicación de hoy; «¿Y si…?» dice sólo lo que cambia.
  if (!c && queda != null && costo != null) {
    const pide = r.pedido?.origen === 'ESTRATEGIA' ? r.pedido.precio : null;
    const decide = r.reglas?.find((x) => x.manda) ?? null;
    if (queda < costo - CASI)
      out.push({
        clave: 'DEBAJO_DEL_COSTO',
        tono: 'bloqueo',
        titulo: 'Queda debajo de tu costo',
        // Auditoría I1: en el historial, si alguien eligió no cumplir el margen, se dice eso.
        texto: `A ${fmt(queda)} perdés ${fmt(redondo(costo - queda))} por unidad. ${
          r.elegida
            ? r.elegida.clave === 'NO_MOVER'
              ? 'En Por decidir se eligió no moverlo.'
              : pisoDelMargen(r) != null
                ? 'En Por decidir se eligió no cumplir tu margen.'
                : 'Una regla «No ganar menos de … %» lo evitaría.'
            : pisoDelMargen(r) != null
              ? 'Tus reglas no lo evitaron.'
              : 'Una regla «No ganar menos de … %» lo evitaría.'
        }`,
      });
    else if (r.estado === 'CALCULADO' && pide != null && pide < costo - CASI)
      out.push({
        clave: 'PERDIDA',
        tono: 'alerta',
        titulo: 'Tu estrategia te hacía vender a pérdida',
        texto: `A ${fmt(pide)} perdías ${fmt(redondo(costo - pide))} por unidad. ${
          decide?.tipo === 'MARGEN_MINIMO'
            ? 'Tu regla de margen lo evitó.'
            : decide || r.reglaQueLimito
              ? `Tu regla «${decide?.ruleName ?? r.reglaQueLimito}» lo evitó.`
              : 'Tus reglas lo evitaron.'
        } Si esto pasa en muchos productos, conviene revisar la estrategia.`,
      });
  }

  // «¿Y si…?» que deja el precio debajo del costo (informe 229): se avisa como hoy,
  // en condicional; la pérdida de la estrategia ya la dice la explicación de arriba.
  if (c && c.con != null && queda != null && costo != null && queda < costo - CASI)
    out.push({
      clave: 'DEBAJO_DEL_COSTO',
      tono: 'bloqueo',
      titulo: 'Quedaría debajo de tu costo',
      texto: `A ${fmt(queda)} perderías ${fmt(redondo(costo - queda))} por unidad. ${
        pisoDelMargen(r) != null
          ? 'Tus reglas no lo evitarían.'
          : 'Una regla «No ganar menos de … %» lo evitaría.'
      }`,
    });

  if (queda != null && r.hoy != null && r.hoy > 0) {
    const p = ((queda - r.hoy) / r.hoy) * 100;
    if (Math.abs(p) >= UMBRAL_DE_SALTO)
      out.push({
        clave: 'SALTO',
        tono: 'alerta',
        titulo: `${p > 0 ? 'Sube' : 'Baja'} bastante de golpe`,
        texto: `Es ${pctConSigno(p)} en un solo cambio. Si no querés saltos así, una regla «No mover más de … % por vez» lo haría de a poco.`,
      });
  }

  if (c && c.con != null && c.sin != null && Math.abs(c.con - c.sin) < CASI)
    out.push({
      clave: 'NADA_QUE_DECIDIR',
      tono: 'ok',
      titulo: 'Nada que decidir en este producto',
      texto: 'Con o sin la regla nueva, queda igual.',
    });
  return out;
}

// ── Las opciones, sin precio posible ───────────────────────────────────────

export interface OpcionSinPrecio {
  clave: ClaveDeSalida;
  /** Los títulos de Por decidir (`tituloDeSalida`). */
  titulo: string;
  precio: string;
  /** Debajo del costo: la tarjeta va con fondo de bloqueo. */
  pierde: boolean;
  elegida: boolean;
  lineas: Array<{ texto: string; malo: boolean }>;
}

/** «queda 27 % arriba del más barato: no cumple la regla nueva (20 %)». */
function noCumple(
  regla: ReglaDelRecorrido | undefined,
  precio: number,
  yaDicho: boolean,
  r: RecorridoDelPrecio
): string {
  const quien = !regla
    ? '«una regla borrada»'
    : regla.probando
      ? 'la regla nueva'
      : `«${regla.ruleName}»`;
  const verbo = yaDicho ? 'tampoco cumple' : 'no cumple';
  if (regla?.tipo === 'SOBRE_EL_MAS_BARATO' && r.masBarato != null) {
    const cuanto =
      !yaDicho && regla.max != null ? ` (${pct((regla.max / r.masBarato - 1) * 100)})` : '';
    return `queda ${frenteAlMasBarato(precio, r.masBarato)}: ${verbo} ${quien}${cuanto}`;
  }
  if (regla?.tipo === 'MOVIMIENTO' && r.hoy != null && r.hoy > 0)
    return `se mueve ${pctConSigno(((precio - r.hoy) / r.hoy) * 100)} desde hoy: ${verbo} ${quien}`;
  return `${verbo} ${quien}`;
}

function opcionesDe(r: RecorridoDelPrecio): OpcionSinPrecio[] | null {
  if (r.estado !== 'SIN_PRECIO_POSIBLE' || !r.salidas || r.salidas.length === 0) return null;
  const enPrueba = r.reglas?.find((x) => x.probando)?.ruleId ?? null;
  const dichas = new Set<string>();
  return r.salidas.map((s) => {
    const lineas: OpcionSinPrecio['lineas'] = [];
    const pierde = r.costo != null && s.precio < r.costo - CASI;
    const m = margenCrudo(s.precio, r.costo);
    if (m == null) lineas.push({ texto: 'margen — (sin costo cargado)', malo: false });
    else if (pierde)
      lineas.push({
        texto: `debajo de tu costo: perdés ${fmt(redondo(r.costo! - s.precio))} por unidad`,
        malo: true,
      });
    else lineas.push({ texto: `ganás ${formatoPorcentaje(m)}`, malo: false });
    const rotas = [...new Set(s.rompe)];
    if (rotas.length === 0) lineas.push({ texto: 'cumple todas tus reglas', malo: false });
    for (const id of rotas) {
      const regla = r.reglas?.find((x) => x.ruleId === id);
      lineas.push({ texto: noCumple(regla, s.precio, dichas.has(id), r), malo: false });
      dichas.add(id);
    }
    // «No mover» con hoy bajo el margen dice lo que se sirve, como en Por decidir.
    if (s.clave === 'NO_MOVER' && r.hoy != null) {
      const propia = explicacionDeSalida(s, r.hoy, r.reglas);
      if (propia !== s.explicacion) lineas.push({ texto: propia, malo: false });
    }
    return {
      clave: s.clave,
      titulo: tituloDeSalida(s.clave, r.reglas, enPrueba),
      precio: fmt(s.precio),
      pierde,
      elegida: r.elegida?.clave === s.clave,
      lineas,
    };
  });
}

// ── Todo junto ─────────────────────────────────────────────────────────────

export interface LecturaDelPrecio {
  /** null sin cálculo: la pantalla dice sólo el porqué del backend (`porQue`). */
  resultado: ResultadoDelPrecio | null;
  porque: Trozo[];
  avisos: AvisoDelPrecio[];
  opciones: OpcionSinPrecio[] | null;
}

/**
 * `sinOpciones`: la pantalla dibuja su propia lista de salidas (la ventana
 * «Decidir»); el porqué no promete «una de estas».
 */
export function lecturaDelPrecio(
  r: RecorridoDelPrecio,
  tiempo: Tiempo,
  { sinOpciones = false }: { sinOpciones?: boolean } = {}
): LecturaDelPrecio {
  return {
    resultado: resultadoDe(r, tiempo),
    porque: porqueDe(r, tiempo, sinOpciones),
    avisos: avisosDe(r),
    opciones: opcionesDe(r),
  };
}
